import { Clock, Filter, MoreHorizontal, Search, User } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { apiUrl } from '@/config/api';

const API_BASE = apiUrl('/api/applications');

interface ApplicationListItem {
  id: string;
  receiptNumber: string;
  status: string;
  submittedAt: string | null;
  applicant: {
    nameFirst: string;
    nameLast: string;
    email: string;
  } | null;
  offering: {
    offering: { name: string };
    structure: { nameFormal: string };
  } | null;
}

export default function ApplicationList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [applications, setApplications] = useState<ApplicationListItem[]>([]);

  useEffect(() => {
    const fetchApplications = async () => {
      try {
        setLoading(true);
        const res = await fetch(API_BASE);
        if (res.ok) {
          const data = (await res.json()) as ApplicationListItem[];
          setApplications(data);
        }
      } catch (error) {
        console.error('Failed to fetch applications', error);
      } finally {
        setLoading(false);
      }
    };
    fetchApplications();
  }, []);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PAID':
        return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">入金済</Badge>;
      case 'SUBMITTED':
        return <Badge className="bg-blue-100 text-blue-700 border-blue-200">出願済</Badge>;
      case 'PAYMENT_PENDING':
        return <Badge className="bg-orange-100 text-orange-700 border-orange-200">未入金</Badge>;
      case 'PASSED':
        return <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200">合格</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">申込情報検索</h1>
          <p className="text-gray-500 mt-1">志願者の出願状況、決済情報を検索・確認します。</p>
        </div>
      </div>

      {/* Toolbar */}
      <Card>
        <CardContent className="p-4 flex flex-wrap gap-4 items-end">
          <div className="space-y-1.5 flex-1 min-w-[240px]">
            <label htmlFor="search-keyword" className="text-sm font-medium text-gray-700">
              キーワード
            </label>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
              <Input id="search-keyword" placeholder="氏名、受付番号、メール..." className="pl-9" />
            </div>
          </div>
          <div className="space-y-1.5 flex-1 min-w-[200px]">
            <label htmlFor="search-exam-type" className="text-sm font-medium text-gray-700">
              入試区分
            </label>
            <Input id="search-exam-type" placeholder="すべての入試" />
          </div>
          <div className="space-y-1.5 w-40">
            <label htmlFor="search-status" className="text-sm font-medium text-gray-700">
              ステータス
            </label>
            <Input id="search-status" placeholder="すべて" />
          </div>
          <Button className="bg-blue-600 hover:bg-blue-700">
            <Filter className="w-4 h-4 mr-2" /> 検索実行
          </Button>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-t-4 border-t-blue-600 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>受付番号</TableHead>
                <TableHead>志願者氏名</TableHead>
                <TableHead>入試区分 / 募集枠</TableHead>
                <TableHead>ステータス</TableHead>
                <TableHead>出願日時</TableHead>
                <TableHead className="text-right">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-gray-400">
                    読み込み中...
                  </TableCell>
                </TableRow>
              ) : applications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-gray-400">
                    対象の申込データが見つかりません。
                  </TableCell>
                </TableRow>
              ) : (
                applications.map((app) => (
                  <TableRow
                    key={app.id}
                    className="group hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/admin/applications/${app.id}`)}
                  >
                    <TableCell className="font-mono text-xs font-bold text-blue-600">
                      {app.receiptNumber || 'DRAFT'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500">
                          <User className="w-4 h-4" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {app.applicant?.nameLast} {app.applicant?.nameFirst}
                          </div>
                          <div className="text-xs text-gray-500">{app.applicant?.email}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div className="text-gray-900">{app.offering?.offering?.name}</div>
                        <div className="text-gray-500 font-xs">{app.offering?.structure?.nameFormal}</div>
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(app.status)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Clock className="w-3 h-3" />
                        {app.submittedAt ? new Date(app.submittedAt).toLocaleString() : '-'}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>{t('common.actions')}</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => navigate(`/admin/applications/${app.id}`)}>
                            詳細を表示
                          </DropdownMenuItem>
                          <DropdownMenuItem>受験票のプレビュー</DropdownMenuItem>
                          <DropdownMenuItem className="text-red-600">削除（無効化）</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
